// Lib
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';

// Actions
import { ClosePopup } from '@actions/popup';

// Hooks
import useWindowSize from '@hooks/useWindowSize';

// Types
import { RootState } from '@app-types/index';

// Fixed header height used as the top offset for mobile full-size popups.
const HEADER_HEIGHT = '64px';

export interface UsePopupReturn {
  display: boolean;
  title: string | null;
  children?: React.ReactNode;
  style: React.CSSProperties | null;
  id: string | null;
  animation: string;
  positiveText: string;
  negativeText: string;
  closable: boolean;
  onCancel: () => void;
  onDone: () => void;
  top: string;
  isMobileFullSize: boolean;
}

const usePopup = (): UsePopupReturn => {
  const dispatch = useDispatch();
  const [title, setTitle] = useState<string>('');
  const { pathname } = useLocation();
  const { onCancel, onDone, isMobileFullSize, ...props } = useSelector(
    (state: RootState) => state.Popup,
  );
  const [top, setTop] = useState<string>('0');
  const { width } = useWindowSize();

  useEffect(() => {
    if (isMobileFullSize && width && width <= 768 && top === '0') {
      setTop(HEADER_HEIGHT);
    }
    if (isMobileFullSize && width && width > 768 && top !== '0') {
      setTop('0');
    }
  }, [width, top, isMobileFullSize]);

  useEffect(() => {
    if (props.display) dispatch(ClosePopup());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pathname]);

  useEffect(() => {
    if (props.title && !title) {
      setTitle(props.title);
    }
  }, [setTitle, props, title]);

  const handleCancel = useCallback((): void => {
    if (props.closable) {
      if (onCancel) {
        onCancel();
      }
      if (title === 'Stats') {
        clearAllBodyScrollLocks();
      }
      dispatch(ClosePopup());
    }
  }, [dispatch, onCancel, props.closable, title]);

  const handleDone = useCallback((): void => {
    if (onDone) {
      onDone();
    }
    dispatch(ClosePopup());
  }, [dispatch, onDone]);

  return {
    ...props,
    animation: props.animation || 'ZoomIn',
    onCancel: handleCancel,
    onDone: handleDone,
    top: top,
    isMobileFullSize,
  };
};

export default usePopup;
